// Countries and time zones for the venue form.
//
// Both come from the browser's own tables rather than a list kept here, so a
// venue anywhere can be registered, which the pilot owners asked for on
// 2026-09-22: the form used to offer nine time zones, seven of them American.

// ISO 3166-1 alpha-2, every assigned code. The names come from Intl, in English,
// and are what gets stored: "New Zealand" is already in the data that way, and a
// dropdown is what keeps the next one from being "NZ" or "new zealand".
const CODES = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
  "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR " +
  "GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP " +
  "KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT " +
  "MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW " +
  "SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG " +
  "UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW"
).split(" ");

/** English country names, sorted. Empty if this browser has no region names. */
export function countryNames(): string[] {
  try {
    const names = new Intl.DisplayNames(["en"], { type: "region" });
    const list = CODES.map((c) => names.of(c)).filter((n): n is string => typeof n === "string" && n.length > 2);
    return [...new Set(list)].sort((a, b) => a.localeCompare(b, "en"));
  } catch {
    return [];
  }
}

// The nine the form offered before, kept for a browser too old to list zones.
const FALLBACK_ZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles",
  "America/Anchorage", "Pacific/Honolulu", "Pacific/Auckland", "Asia/Kolkata",
];

/** Every region/city zone the browser knows. Plain offsets like "UTC" are left
    out: the database requires a region, because an offset cannot follow a
    venue's daylight saving, and lunch versus dinner is the study's main axis. */
export function timeZones(): string[] {
  try {
    const zones = Intl.supportedValuesOf("timeZone").filter((z) => z.includes("/") && !z.startsWith("Etc/"));
    return zones.length > 0 ? zones : FALLBACK_ZONES;
  } catch {
    return FALLBACK_ZONES;
  }
}

/** Zones grouped by their first part ("America", "Pacific"), for optgroups. */
export function groupZones(zones: readonly string[]): [string, string[]][] {
  const groups = new Map<string, string[]>();
  for (const z of zones) {
    const region = z.slice(0, z.indexOf("/"));
    groups.set(region, [...(groups.get(region) ?? []), z]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/** Text as the database's shape checks want it: no leading or trailing space,
    runs of whitespace as one space, no control characters (C0, DEL or C1). */
export function tidy(text: string): string {
  return text.replace(/[\u0000-\u001f\u007f-\u009f]/g, " ").replace(/\s+/g, " ").trim();
}
